import { TripEmission, CarState } from "../types/cars";

export const metersToKm = (meters: number) => meters / 1000;

const KG_CO2_SAVED_PER_TREE = 25;

/**
 * National / regional grid keys used for EV well-to-wheel estimates.
 * Intensity table: Norway/France keep existing Shed factors; others aligned to
 * Ember Yearly Electricity–style annual averages (g CO₂/kWh, ~2024–2025).
 */
export type ElectricGridCountry =
  | "norway"
  | "france"
  | "uk"
  | "germany"
  | "sweden"
  | "spain"
  | "italy"
  | "netherlands"
  | "us"
  | "canada"
  | "brazil"
  | "japan"
  | "china"
  | "india"
  | "australia"
  | "world";

const ELECTRIC_GRID_CARBON_INTENSITY_CO2GRAM_PER_KWH: Record<
  ElectricGridCountry,
  number
> = {
  norway: 29,
  france: 22,
  uk: 177,
  germany: 330,
  sweden: 35,
  spain: 160,
  italy: 280,
  netherlands: 280,
  us: 350,
  canada: 120,
  brazil: 110,
  japan: 490,
  china: 530,
  india: 670,
  australia: 480,
  world: 440,
};

const ELECTRIC_GRID_LABELS: Record<ElectricGridCountry, string> = {
  norway: "Norwegian",
  france: "French",
  uk: "UK",
  germany: "German",
  sweden: "Swedish",
  spain: "Spanish",
  italy: "Italian",
  netherlands: "Dutch",
  us: "US",
  canada: "Canadian",
  brazil: "Brazilian",
  japan: "Japanese",
  china: "Chinese",
  india: "Indian",
  australia: "Australian",
  world: "global average",
};

export const getElectricGridIntensity = (
  gridCountry: ElectricGridCountry
): number => ELECTRIC_GRID_CARBON_INTENSITY_CO2GRAM_PER_KWH[gridCountry];

export const getElectricGridLabel = (
  gridCountry: ElectricGridCountry
): string => ELECTRIC_GRID_LABELS[gridCountry];

/**
 * KiloEmission - Car Type [Gas]
 */
const gasEmissionKiloC02 = (gramEmission: number, distanceMeters: number) => {
  // Convert distance from meters to kilometers
  const distanceKm = distanceMeters / 1000;

  // Total CO₂ emissions in grams for the distance (g/km * km)
  const totalEmissionsGrams = gramEmission * distanceKm;

  // Convert grams to kilograms
  const totalEmissionsKg = totalEmissionsGrams / 1000;

  return totalEmissionsKg; // Emissions in kilograms
};

/**
 * KiloEmission - Car Type [Electric]
 */
export const electricEmissionKiloCo2 = (
  gramEmission: number,
  distanceMeters: number,
  gridCountry: ElectricGridCountry = "norway"
) => {
  const distanceKm = distanceMeters / 1000;

  // Convert gramEmission (Wh/km) to kWh/km by dividing by 1000
  const energyConsumptionKWhPerKm = gramEmission / 1000;

  // Total energy consumption in kWh for the given distance
  const totalEnergyConsumptionKWh = energyConsumptionKWhPerKm * distanceKm;

  const gridIntensity = getElectricGridIntensity(gridCountry);

  // Total CO₂ emissions in grams (g CO₂ = kWh * intensity)
  const totalEmissionsGrams = totalEnergyConsumptionKWh * gridIntensity;

  // Convert grams to kilograms
  const totalEmissionsKg = totalEmissionsGrams / 1000;

  return totalEmissionsKg; // Emissions in kilograms
};

type EmissionKiloCo2Params = {
  gramEmission: number;
  emissionType: string;
  distanceMeters: number;
  gridCountry?: ElectricGridCountry;
};

/**
 * KiloEmission - Car Type [Any]
 */
const emissionKiloCo2 = ({
  gramEmission,
  emissionType,
  distanceMeters,
  gridCountry = "norway",
}: EmissionKiloCo2Params) => {
  const emissionsKG =
    emissionType === "electric"
      ? electricEmissionKiloCo2(gramEmission, distanceMeters, gridCountry)
      : gasEmissionKiloC02(gramEmission, distanceMeters);

  return emissionsKG;
};

type EmissionToTreesParams = {
  distanceMeters: number;
  emissionType: string;
  gramEmission: number;
  gridCountry?: ElectricGridCountry;
};

/**
 * Convert gramEmission to Trees saved
 */
export const emissionToTrees = ({
  distanceMeters,
  emissionType,
  gramEmission,
  gridCountry = "norway",
}: EmissionToTreesParams): number => {
  // Calculate the total emissions in kg using the distance
  const emissionsInKg = emissionKiloCo2({
    gramEmission,
    emissionType,
    distanceMeters,
    gridCountry,
  });

  // Calculate the number of trees needed to offset the total emissions
  return emissionsInKg / KG_CO2_SAVED_PER_TREE;
};

/** Convert CO₂ kilograms to rainforest tree-year equivalents. */
export const kgToTrees = (kgEmission: number): number =>
  kgEmission / KG_CO2_SAVED_PER_TREE;

type TripEmissionOptions = {
  gridCountry?: ElectricGridCountry;
};

export const tripEmission = (
  car: CarState | null,
  distanceMeters: number,
  options: TripEmissionOptions = {}
): TripEmission => {
  const { gridCountry = "norway" } = options;
  const emissionType = car?.emissionType ?? "";
  const gramEmission = car?.emissionCO2 ?? 0;

  const totalKgEmission = emissionKiloCo2({
    gramEmission,
    emissionType,
    distanceMeters,
    gridCountry,
  });

  const totalTrees = emissionToTrees({
    distanceMeters,
    emissionType,
    gramEmission,
    gridCountry,
  });

  return { distanceMeters, totalTrees, totalKgEmission };
};
